import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { APP_CONFIG } from "../../../constants/constants";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../../utils/dateTime";
import { screenCellKeyFor } from "./eventStacks";
import {
  createEventStackListOverlay,
  StackedEventRow,
} from "./eventStackListOverlay";

// Long enough to cross the gap from the pin to the list without it
// vanishing, short enough that sweeping the pointer over a stack and
// carrying on doesn't leave a panel hanging around.
const CLOSE_DELAY_MS = 350;

export type StackablePin = {
  marker: any;
  point: VehicleMapDataPoint;
  shown: boolean;
};

// The events behind one marker, listed so each can be reached. Two things
// put several events behind one marker: a run of identical reports
// collapsed to a single pin, and unrelated pins landing on the same few
// screen pixels. Same question either way - "what is actually here?" - so
// the same list answers both.
//
// Nothing is drawn on the map for this. A position with one event behaves
// exactly as it did.
export const createEventStackController = <P extends StackablePin>({
  google,
  map,
  timezone = APP_CONFIG.DEFAULT_TIMEZONE,
  getPins,
  isPinVisible,
  labelFor,
  detailFor,
  onHoverPoint,
  onSelectPoint,
}: {
  google: any;
  map: any;
  timezone?: string;
  // the renderer's current detail pins, whatever it has right now
  getPins: () => P[];
  isPinVisible: (pin: P) => boolean;
  labelFor: (point: VehicleMapDataPoint) => string;
  // optional second line for a row - the multi-vehicle map uses it to say
  // which asset the event belongs to, since a stack there can mix vehicles
  detailFor?: (point: VehicleMapDataPoint) => string | undefined;
  onHoverPoint: (point: VehicleMapDataPoint | null) => void;
  onSelectPoint: (point: VehicleMapDataPoint) => void;
}) => {
  let overlay: any = null;
  // rows are addressed by index into the list that is open
  let openPoints: VehicleMapDataPoint[] = [];
  // what the open list is for, so re-hovering the same thing doesn't tear
  // it down and rebuild it under the pointer
  let openKey: string | null = null;
  let closeTimer: ReturnType<typeof setTimeout> | null = null;

  const cancelClose = () => {
    if (closeTimer === null) return;
    clearTimeout(closeTimer);
    closeTimer = null;
  };

  const closeList = () => {
    cancelClose();
    openKey = null;
    if (!overlay) return;
    overlay.setMap(null);
    overlay = null;
    openPoints = [];
  };

  // the pointer has left the marker or the list - give it a moment to
  // arrive somewhere that should keep the list up before taking it away
  const scheduleClose = () => {
    if (!overlay) return;
    cancelClose();
    closeTimer = setTimeout(closeList, CLOSE_DELAY_MS);
  };

  const openList = (points: VehicleMapDataPoint[], key: string) => {
    closeList();
    openKey = key;
    openPoints = points;
    const rows: StackedEventRow[] = points.map((point, index) => ({
      id: `${index}`,
      point,
      label: labelFor(point),
      detail: detailFor?.(point),
      time: formattedDateTimeBasedOnTimezone(
        dbTimeToJsTime(point.timestamp),
        timezone,
        "h:mm:ss aa"
      ),
    }));
    overlay = createEventStackListOverlay({
      google,
      map,
      position: { lat: points[0].latitude, lng: points[0].longitude },
      rows,
      onHoverEvent: (id) =>
        onHoverPoint(id === null ? null : (openPoints[Number(id)] ?? null)),
      onSelectEvent: (id) => {
        const point = openPoints[Number(id)];
        if (!point) return;
        // the event stays marked on the map behind the list, and the list
        // gets out of the way so the detail panel is readable
        onSelectPoint(point);
        closeList();
      },
      onPointerEnter: cancelClose,
      onPointerLeave: scheduleClose,
      onClose: closeList,
    });
    overlay.setMap(map);
  };

  const pointKey = (point: VehicleMapDataPoint) =>
    `${point.recordid ?? point.timestamp}`;

  // The events a collapsed run stands for. The caller already knows them -
  // they were grouped when the pins were built.
  const openRun = (points: VehicleMapDataPoint[]): boolean => {
    if (points.length < 2) return false;
    const key = `run:${pointKey(points[0])}`;
    if (openKey === key) {
      cancelClose();
      return true;
    }
    openList(points, key);
    return true;
  };

  // Unrelated pins that happen to land on the same few pixels at this
  // zoom. Worked out on the hover rather than kept as an index: zoom
  // changes the answer, pins are rebuilt constantly, and one pass over the
  // pins costs far less than keeping a map of them correct through that.
  const openStackFor = (pin: P): boolean => {
    const zoom = map.getZoom();
    if (typeof zoom !== "number") return false;
    const cell = screenCellKeyFor(pin.point, zoom);
    const key = `cell:${cell}`;
    if (openKey === key) {
      cancelClose();
      return true;
    }
    const stack = getPins().filter(
      (candidate) =>
        isPinVisible(candidate) &&
        screenCellKeyFor(candidate.point, zoom) === cell
    );
    if (stack.length < 2) {
      // a lone marker under the pointer means whatever is open is no
      // longer what is being looked at
      scheduleClose();
      return false;
    }
    // oldest first, so the list reads in the order the events happened
    stack.sort((a, b) => a.point.timestamp - b.point.timestamp);
    openList(
      stack.map((candidate) => candidate.point),
      key
    );
    return true;
  };

  return { openRun, openStackFor, scheduleClose, closeList, destroy: closeList };
};
